<script>
    import { createEventDispatcher } from "svelte";
    import {
        MASTER_SALES_COLUMNS,
        metricRowDefs,
        salesColumnConfig
    } from "./store.js";
    import { fade, scale } from "svelte/transition";
    import toast from "svelte-french-toast";

    export let isOpen = false;
    const dispatch = createEventDispatcher();

    // 모달 내부 편집용 로컬 상태
    let localVisibleColIds = [];
    let localVisibleMetricIds = [];
    let selectedOrderIndex = -1; // 하단 순서 변경용으로 선택된 컬럼 인덱스

    $: if (isOpen) {
        localVisibleColIds = [...$salesColumnConfig.visibleColumnIds];
        localVisibleMetricIds = [...$salesColumnConfig.visibleMetricIds];
        selectedOrderIndex = -1;
    }

    function toggleColumn(colId) {
        if (localVisibleColIds.includes(colId)) {
            // 최소 1개는 유지
            if (localVisibleColIds.length <= 1) {
                toast.error("최소 1개 이상의 컬럼을 선택해야 합니다.");
                return;
            }
            localVisibleColIds = localVisibleColIds.filter(id => id !== colId);
            if (selectedOrderIndex >= localVisibleColIds.length) {
                selectedOrderIndex = localVisibleColIds.length - 1;
            }
        } else {
            localVisibleColIds = [...localVisibleColIds, colId];
        }
    }

    function toggleMetric(metricId) {
        if (localVisibleMetricIds.includes(metricId)) {
            if (localVisibleMetricIds.length <= 1) {
                toast.error("최소 1개 이상의 항목을 선택해야 합니다.");
                return;
            }
            localVisibleMetricIds = localVisibleMetricIds.filter(id => id !== metricId);
        } else {
            localVisibleMetricIds = [...localVisibleMetricIds, metricId];
        }
    }

    // 컬럼 순서 왼쪽으로 이동 (◀)
    function moveLeft() {
        if (selectedOrderIndex > 0) {
            const arr = [...localVisibleColIds];
            const temp = arr[selectedOrderIndex - 1];
            arr[selectedOrderIndex - 1] = arr[selectedOrderIndex];
            arr[selectedOrderIndex] = temp;
            localVisibleColIds = arr;
            selectedOrderIndex--;
        }
    }

    // 컬럼 순서 오른쪽으로 이동 (▶)
    function moveRight() {
        if (selectedOrderIndex >= 0 && selectedOrderIndex < localVisibleColIds.length - 1) {
            const arr = [...localVisibleColIds];
            const temp = arr[selectedOrderIndex + 1];
            arr[selectedOrderIndex + 1] = arr[selectedOrderIndex];
            arr[selectedOrderIndex] = temp;
            localVisibleColIds = arr;
            selectedOrderIndex++;
        }
    }

    function handleApply() {
        salesColumnConfig.set({
            visibleColumnIds: [...localVisibleColIds],
            visibleMetricIds: [...localVisibleMetricIds],
        });
        toast.success("컬럼 및 항목 설정이 적용되었습니다.", { duration: 1500 });
        dispatch("close");
    }

    function handleClose() {
        dispatch("close");
    }

    function getColLabel(id) {
        const found = MASTER_SALES_COLUMNS.find(c => c.id === id);
        return found ? found.label : id;
    }
</script>

{#if isOpen}
    <div class="modal-backdrop" on:click={handleClose} transition:fade={{ duration: 150 }}>
        <div class="modal-box" on:click|stopPropagation transition:scale={{ duration: 180, start: 0.95 }}>
            <!-- 모달 헤더 -->
            <div class="modal-header">
                <div class="modal-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="4" y1="21" x2="4" y2="14"/>
                        <line x1="4" y1="10" x2="4" y2="3"/>
                        <line x1="12" y1="21" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12" y2="3"/>
                        <line x1="20" y1="21" x2="20" y2="16"/>
                        <line x1="20" y1="12" x2="20" y2="3"/>
                        <line x1="1" y1="14" x2="7" y2="14"/>
                        <line x1="9" y1="8" x2="15" y2="8"/>
                        <line x1="17" y1="16" x2="23" y2="16"/>
                    </svg>
                    <span>컬럼 구성 - 컬럼 추가/숨기기</span>
                </div>
                <button class="close-btn" on:click={handleClose}>✕</button>
            </div>

            <div class="modal-body">
                <!-- 좌우 분할: 좌) 제품 컬럼, 우) 표시 항목 -->
                <div class="modal-columns">
                    <section class="panel">
                        <div class="section-title">
                            <span>제품 컬럼</span>
                            <span class="section-sub">(체크하면 아래 순서 바에 추가됩니다)</span>
                        </div>
                        <div class="check-grid">
                            {#each MASTER_SALES_COLUMNS as col}
                                <label class="check-card" class:checked={localVisibleColIds.includes(col.id)}>
                                    <input
                                        type="checkbox"
                                        checked={localVisibleColIds.includes(col.id)}
                                        on:change={() => toggleColumn(col.id)}
                                    />
                                    <span class="col-name">{col.label}</span>
                                    {#if localVisibleColIds.includes(col.id)}
                                        <span class="col-badge">선택됨</span>
                                    {/if}
                                </label>
                            {/each}
                        </div>
                    </section>

                    <section class="panel">
                        <div class="section-title">
                            <span>항목</span>
                            <span class="section-sub">(체크 해제한 항목은 표에서 숨겨집니다)</span>
                        </div>
                        <div class="check-grid">
                            {#each $metricRowDefs as m}
                                <label class="check-card" class:checked={localVisibleMetricIds.includes(m.id)}>
                                    <input
                                        type="checkbox"
                                        checked={localVisibleMetricIds.includes(m.id)}
                                        on:change={() => toggleMetric(m.id)}
                                    />
                                    <span class="col-name">{m.label}</span>
                                    {#if localVisibleMetricIds.includes(m.id)}
                                        <span class="col-badge">표시</span>
                                    {/if}
                                </label>
                            {/each}
                        </div>
                    </section>
                </div>

                <!-- 하단 선택된 컬럼 순서 배치 및 이동 컨트롤 -->
                <section class="order-section">
                    <div class="section-title">
                        <span>표시 컬럼 순서</span>
                        <span class="section-sub">(컬럼을 클릭한 뒤 ◀ ▶ 버튼으로 이동)</span>
                    </div>
                    <div class="order-row">
                        <div class="order-chips-wrap">
                            {#each localVisibleColIds as colId, idx}
                                <button
                                    type="button"
                                    class="order-chip"
                                    class:selected={selectedOrderIndex === idx}
                                    on:click={() => selectedOrderIndex = idx}
                                >
                                    {getColLabel(colId)}
                                </button>
                            {/each}
                        </div>
                        <div class="order-controls">
                            <button
                                type="button"
                                class="move-btn"
                                on:click={moveLeft}
                                disabled={selectedOrderIndex <= 0}
                                title="왼쪽(앞)으로 이동"
                            >
                                ◀
                            </button>
                            <button
                                type="button"
                                class="move-btn"
                                on:click={moveRight}
                                disabled={selectedOrderIndex < 0 || selectedOrderIndex >= localVisibleColIds.length - 1}
                                title="오른쪽(뒤)으로 이동"
                            >
                                ▶
                            </button>
                        </div>
                    </div>
                </section>
            </div>

            <!-- 푸터 액션 -->
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" on:click={handleClose}>닫기</button>
                <button type="button" class="btn btn-primary" on:click={handleApply}>적용</button>
            </div>
        </div>
    </div>
{/if}

<style>
    .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.6);
        backdrop-filter: blur(2px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
        padding: 16px;
    }
    .modal-box {
        background: #ffffff;
        width: 100%;
        max-width: 960px;
        max-height: 92vh;
        border-radius: 8px;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border: 1px solid #cbd5e1;
    }
    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 20px;
        background: #1e293b;
        color: white;
    }
    .modal-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 1rem;
        font-weight: 800;
    }
    .close-btn {
        background: transparent;
        border: none;
        color: #94a3b8;
        font-size: 1.15rem;
        cursor: pointer;
    }
    .close-btn:hover {
        color: white;
    }

    .modal-body {
        padding: 18px 20px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        overflow-y: auto;
    }
    .modal-columns {
        display: grid;
        grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
        gap: 16px;
    }
    .panel {
        border: 1.5px solid #e2e8f0;
        border-radius: 6px;
        padding: 14px;
        background: #ffffff;
        min-width: 0;
    }
    .section-title {
        display: flex;
        align-items: baseline;
        gap: 8px;
        font-size: 0.9rem;
        font-weight: 800;
        color: #1e293b;
        margin-bottom: 10px;
    }
    .section-sub {
        font-size: 0.72rem;
        font-weight: 600;
        color: #94a3b8;
    }

    /* 체크 카드 그리드 */
    .check-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
    }
    .check-card {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        border: 1.5px solid #e2e8f0;
        border-radius: 6px;
        background: #ffffff;
        cursor: pointer;
        user-select: none;
        transition: all 0.12s;
        min-width: 0;
    }
    .check-card:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
    }
    .check-card.checked {
        background: #eff6ff;
        border-color: #93c5fd;
    }
    .check-card input {
        width: 16px;
        height: 16px;
        margin: 0;
        cursor: pointer;
        flex-shrink: 0;
    }
    .col-name {
        flex: 1;
        min-width: 0;
        font-size: 0.86rem;
        font-weight: 700;
        color: #1e293b;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .col-badge {
        font-size: 0.64rem;
        font-weight: 800;
        color: #1d4ed8;
        background: #dbeafe;
        padding: 1px 6px;
        border-radius: 8px;
        flex-shrink: 0;
    }

    /* 하단 순서 변경 섹션 */
    .order-section {
        border: 1.5px solid #e2e8f0;
        border-radius: 6px;
        padding: 14px;
        background: #f8fafc;
    }
    .order-row {
        display: flex;
        align-items: center;
        gap: 14px;
    }
    .order-chips-wrap {
        flex: 1;
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        min-height: 46px;
        padding: 6px;
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 6px;
        align-items: center;
    }
    .order-chip {
        padding: 6px 14px;
        background: #ffffff;
        border: 1.5px solid #cbd5e1;
        border-radius: 4px;
        font-size: 0.84rem;
        font-weight: 700;
        color: #334155;
        cursor: pointer;
        transition: all 0.15s;
    }
    .order-chip:hover {
        background: #eff6ff;
        border-color: #93c5fd;
    }
    .order-chip.selected {
        background: #2563eb;
        color: white;
        border-color: #1d4ed8;
        box-shadow: 0 1px 3px rgba(37, 99, 235, 0.3);
    }
    .order-controls {
        display: flex;
        gap: 10px;
        flex-shrink: 0;
    }
    .move-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 1.5px solid #cbd5e1;
        background: #ffffff;
        color: #2563eb;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.9rem;
        cursor: pointer;
        transition: all 0.15s;
    }
    .move-btn:hover:not(:disabled) {
        background: #eff6ff;
        border-color: #2563eb;
    }
    .move-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    /* 푸터 */
    .modal-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        padding: 12px 20px;
        background: #f8fafc;
        border-top: 1px solid #e2e8f0;
    }
    .btn {
        padding: 7px 22px;
        border-radius: 4px;
        font-size: 0.85rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s;
    }
    .btn-secondary {
        background: #ffffff;
        border: 1px solid #cbd5e1;
        color: #475569;
    }
    .btn-secondary:hover {
        background: #f1f5f9;
    }
    .btn-primary {
        background: #2563eb;
        border: 1px solid #1d4ed8;
        color: white;
    }
    .btn-primary:hover {
        background: #1d4ed8;
    }

    @media (max-width: 800px) {
        .modal-columns {
            grid-template-columns: minmax(0, 1fr);
        }
        .order-row {
            flex-direction: column;
            align-items: stretch;
        }
        .order-controls {
            justify-content: center;
        }
    }
</style>
